import type {
	AcordExistingDataResult,
	AcordExtractionResult,
	AcordResultModel,
} from "@/types/api/acord";
import type {
	ClientDetail,
	ClientSummary,
	CreateClientRequest,
	CreateClientResult,
	UpdateClientRequest,
} from "@/types/api/client";
import type { ApiResponse, PaginatedResult } from "@/types/api/common";
import { apiFetch } from "./api-client";

export const clientService = {
	/**
	 * Create a new client tenant (`POST /clients`). Runs the full
	 * TenantProvisioningService path: held owner + Admin role, Entra group
	 * (CIAM), billing trial, optional starter pack. The result carries the
	 * fail-open `warnings[]` the caller must surface. 409 = name conflict /
	 * provisioning still running, 400 = provisioning precondition (e.g. owner
	 * e-mail in use), 422 = request validation (duplicate name/domain, …).
	 */
	createClient: async (
		data: CreateClientRequest,
	): Promise<CreateClientResult> => {
		const response = await apiFetch<ApiResponse<CreateClientResult>>(
			"/clients",
			{
				method: "POST",
				body: JSON.stringify(data),
			},
		);
		return response.result;
	},

	/**
	 * Get paginated list of clients
	 */
	getClients: async (
		page = 1,
		pageSize = 25,
		search = "",
	): Promise<PaginatedResult<ClientSummary>> => {
		const params = new URLSearchParams({
			page: page.toString(),
			pageSize: pageSize.toString(),
			search,
		});

		const response = await apiFetch<
			ApiResponse<PaginatedResult<ClientSummary>>
		>(`/clients?${params}`);
		return response.result;
	},

	/**
	 * Get single client details
	 */
	getClientById: async (id: string): Promise<ClientDetail> => {
		const response = await apiFetch<ApiResponse<ClientDetail>>(
			`/clients/${id}`,
		);
		return response.result;
	},

	updateClient: async (
		id: string,
		data: UpdateClientRequest,
	): Promise<ClientSummary> => {
		const response = await apiFetch<ApiResponse<ClientSummary>>(
			`/clients/${id}`,
			{
				method: "PUT",
				body: JSON.stringify(data),
			},
		);
		return response.result;
	},

	/**
	 * Extract ACORD data from a file for a specific client
	 */
	extractAcordData: async (
		clientId: string,
		file: File,
	): Promise<AcordExtractionResult> => {
		const formData = new FormData();
		formData.append("file", file);

		const response = await apiFetch<ApiResponse<AcordResultModel>>(
			`/clients/${clientId}/acord/extract`,
			{
				method: "POST",
				body: formData,
			},
		);

		if (!response.result.success || !response.result.data) {
			throw new Error(
				response.result.errorMessage || "Failed to extract data",
			);
		}

		return response.result.data;
	},

	/**
	 * Get existing extracted ACORD data for a client
	 */
	getAcordData: async (
		clientId: string,
	): Promise<AcordExtractionResult | null> => {
		const response = await apiFetch<ApiResponse<AcordExistingDataResult>>(
			`/clients/${clientId}/acord`,
		);

		// The GET endpoint returns a specific wrapper (ResultModel) which doesn't have the standard 'success' flag
		// If we get here (status 200 via apiFetch), we assume success.
		// Use optional chaining or check for data existence
		if (!response.result || !response.result.data) {
			return null;
		}

		return response.result.data;
	},

	/**
	 * Initialize default roles for a client
	 */
	initializeDefaultRoles: async (clientId: string): Promise<void> => {
		await apiFetch("/clients/onboard/roles", {
			method: "POST",
			body: JSON.stringify({ clientId }),
		});
	},
};
